import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import "./AddBlog.css";
import BannerAds from "./BannerAds";
// SECURITY (10.1): device fingerprint sent with a blog publish.
import { getDeviceFingerprint } from "../utils/deviceFingerprint";
import { getContentPreferences, saveContentPreferences, FALLBACK_OPTIONS } from "../services/preferencesApi";
import { getPayoutSetup } from "../services/treatApi";
import "./Treat/Treat.css";

// ─── filters ───────────────────────────────────────
// FILTERS, CropEditor, ADJUST_SLIDERS, DEFAULT_ADJUST, buildImageFilter and
// bakeEditedImage are exported so the Pro composer (Pro/ProCreateContent.jsx)
// offers exactly this photo editor instead of a second implementation.
export const FILTERS = [
  { name: "Normal", css: "none" },
  { name: "Clarendon", css: "contrast(1.2) saturate(1.35) brightness(1.05)" },
  { name: "Gingham", css: "brightness(1.06) hue-rotate(-10deg) contrast(0.9)" },
  { name: "Moon", css: "grayscale(1) contrast(1.1) brightness(1.1)" },
  { name: "Lark", css: "brightness(1.12) contrast(0.85) saturate(1.2)" },
  { name: "Reyes", css: "sepia(0.22) brightness(1.1) contrast(0.85) saturate(0.75)" },
  { name: "Juno", css: "saturate(1.4) contrast(1.1) brightness(1.05) hue-rotate(-5deg)" },
  { name: "Slumber", css: "saturate(0.7) brightness(1.05) sepia(0.15)" },
  { name: "Crema", css: "saturate(0.85) contrast(0.95) brightness(1.12) sepia(0.1)" },
  { name: "Ludwig", css: "saturate(0.9) contrast(1.05) brightness(1.08) sepia(0.06)" },
  { name: "Aden", css: "hue-rotate(-20deg) saturate(0.85) brightness(1.2) contrast(0.85)" },
  { name: "Perpetua", css: "saturate(1.1) contrast(1.1) brightness(1.02)" },
  { name: "Neon", css: "saturate(2.4) hue-rotate(30deg) contrast(1.25)" },
  { name: "Drama", css: "contrast(1.5) brightness(0.86) saturate(1.35)" },
  { name: "Fade", css: "brightness(1.14) contrast(0.76) saturate(0.7)" },
  { name: "Vintage", css: "sepia(0.55) contrast(1.15) brightness(0.88)" },
];

// ─── Crop ratios ───────────────────────────────────────────────────
const RATIOS = [
  { label: "Free", value: 0, icon: "⛶" },
  { label: "1:1", value: 1, icon: "□" },
  { label: "4:5", value: 4/5, icon: "▯" },
  { label: "9:16", value: 9/16, icon: "▮" },
  { label: "16:9", value: 16/9, icon: "▬" },
];

// ─── Adjust sliders ────────────────────────────────────────────────
export const DEFAULT_ADJUST = {
  brightness:100, contrast:100, saturation:100, warmth:0, fade:0, vignette:0,
};

export const ADJUST_SLIDERS = [
  { key:"brightness", label:"Brightness ☀", min:50, max:150, step:1 },
  { key:"contrast", label:"Contrast ◑", min:50, max:180, step:1 },
  { key:"saturation", label:"Saturation 🎨", min:0, max:220, step:1 },
  { key:"warmth", label:"Warmth 🔥", min:0, max:100, step:1 },
  { key:"fade", label:"Fade 🌫", min:0, max:100, step:1 },
  { key:"vignette", label:"Vignette 🔮", min:0, max:100, step:1 },
];

// CSS filter string for a filter preset index + adjust values.
export const buildImageFilter = (fi, adj) => {
  const base = FILTERS[fi].css === "none"? "" : FILTERS[fi].css;
  const warm = adj.warmth > 0? `hue-rotate(${adj.warmth*-0.3}deg) sepia(${adj.warmth*0.008})` : "";
  const fade = adj.fade > 0? `brightness(${1+adj.fade*0.002}) contrast(${1-adj.fade*0.005}) saturate(${1-adj.fade*0.006})` : "";
  return [`brightness(${adj.brightness/100})`,`contrast(${adj.contrast/100})`,`saturate(${adj.saturation/100})`,warm,fade,base].filter(Boolean).join(" ");
};

// Renders the image with its filter (and vignette) into a JPEG blob.
export const bakeEditedImage = async (srcUrl, filterCss, vignette) => {
  const img = new Image(); img.src = srcUrl;
  await new Promise(r => (img.onload = r));
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.filter = filterCss;
  ctx.drawImage(img, 0, 0);
  if (vignette > 0) {
    const g = ctx.createRadialGradient(canvas.width/2,canvas.height/2,canvas.width*.3,canvas.width/2,canvas.height/2,canvas.width*.75);
    g.addColorStop(0,"rgba(0,0,0,0)");
    g.addColorStop(1,`rgba(0,0,0,${vignette/120})`);
    ctx.filter = "none"; ctx.fillStyle = g;
    ctx.fillRect(0,0,canvas.width,canvas.height);
  }
  return new Promise(res => canvas.toBlob(blob => res(blob), "image/jpeg", 0.92));
};

// ══════════════════════════════════════════════════════════════
// CROP EDITOR COMPONENT
// ══════════════════════════════════════
export function CropEditor({ src, onDone, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const cropRef = useRef({ x:0, y:0, w:0, h:0 });
  const dragRef = useRef(null);
  const [ratio, setRatio] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const HANDLE = 14;

  const draw = useCallback(() => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!canvas ||!img ||!imgLoaded) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { x, y, w, h } = cropRef.current;
    ctx.fillStyle = "rgba(0,0,0,0.58)";
    ctx.fillRect(0,0,canvas.width,y);
    ctx.fillRect(0,y+h,canvas.width,canvas.height-y-h);
    ctx.fillRect(0,y,x,h);
    ctx.fillRect(x+w,y,canvas.width-x-w,h);
    ctx.strokeStyle="#a78bfa"; ctx.lineWidth=2;
    ctx.strokeRect(x,y,w,h);
    ctx.strokeStyle="rgba(167,139,250,0.35)"; ctx.lineWidth=0.8;
    for(let i=1;i<=2;i++){
      ctx.beginPath();ctx.moveTo(x+w*i/3,y);ctx.lineTo(x+w*i/3,y+h);ctx.stroke();
      ctx.beginPath();ctx.moveTo(x,y+h*i/3);ctx.lineTo(x+w,y+h*i/3);ctx.stroke();
    }
    getHandles().forEach(({hx,hy})=>{
      ctx.fillStyle="#a78bfa";
      ctx.beginPath();ctx.arc(hx,hy,HANDLE/2,0,Math.PI*2);ctx.fill();
    });
    const L=18; ctx.strokeStyle="#fff"; ctx.lineWidth=2.5;
    [[x,y,L,0,0,L],[x+w,y,-L,0,0,L],[x,y+h,L,0,0,-L],[x+w,y+h,-L,0,0,-L]].forEach(([cx,cy,d1,d2,d3,d4])=>{
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.lineTo(cx+d1,cy+d2);ctx.stroke();
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.lineTo(cx+d3,cy+d4);ctx.stroke();
    });
  }, [imgLoaded]);

  const getHandles = () => {
    const{x,y,w,h}=cropRef.current;
    return[
      {id:"tl",hx:x, hy:y}, {id:"tr",hx:x+w, hy:y},
      {id:"bl",hx:x, hy:y+h}, {id:"br",hx:x+w, hy:y+h},
      {id:"tm",hx:x+w/2,hy:y}, {id:"bm",hx:x+w/2,hy:y+h},
      {id:"ml",hx:x, hy:y+h/2},{id:"mr",hx:x+w, hy:y+h/2},
    ];
  };

  const initCrop = useCallback(()=>{
    const canvas=canvasRef.current;
    if(!canvas||!imgLoaded) return;
    const CW=canvas.width,CH=canvas.height;
    if(ratio===0){cropRef.current={x:CW*.05,y:CH*.05,w:CW*.9,h:CH*.9};}
    else{
      let cw,ch;
      if(CW/CH>ratio){ch=CH*.9;cw=ch*ratio;}else{cw=CW*.9;ch=cw/ratio;}
      cropRef.current={x:(CW-cw)/2,y:(CH-ch)/2,w:cw,h:ch};
    }
    draw();
  },[ratio,imgLoaded,draw]);

  useEffect(()=>{initCrop();},[initCrop]);

  useEffect(()=>{
    const img=new Image();
    img.onload=()=>{
      imgRef.current=img;
      const con=containerRef.current,canvas=canvasRef.current;
      if(!con||!canvas) return;
      const mW=con.clientWidth||480,mH=window.innerHeight*.5;
      const sc=Math.min(mW/img.naturalWidth,mH/img.naturalHeight,1);
      canvas.width=Math.round(img.naturalWidth*sc);
      canvas.height=Math.round(img.naturalHeight*sc);
      setImgLoaded(true);
    };
    img.src=src;
  },[src]);

  useEffect(()=>{if(imgLoaded)draw();},[imgLoaded,draw]);

  const getPos=(e,canvas)=>{
    const r=canvas.getBoundingClientRect();
    const sx=canvas.width/r.width,sy=canvas.height/r.height;
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    return{x:(cx-r.left)*sx,y:(cy-r.top)*sy};
  };
  const hitHandle=(px,py)=>getHandles().find(({hx,hy})=>Math.hypot(px-hx,py-hy)<HANDLE+4);
  const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
  const MIN=40;

  const onDown=(e)=>{
    e.preventDefault();
    const canvas=canvasRef.current;
    const{x:px,y:py}=getPos(e,canvas);
    const hit=hitHandle(px,py);
    if(hit){dragRef.current={type:"resize",handle:hit.id,startX:px,startY:py,startCrop:{...cropRef.current}};}
    else{
      const{x,y,w,h}=cropRef.current;
      if(px>=x&&px<=x+w&&py>=y&&py<=y+h)
        dragRef.current={type:"move",startX:px,startY:py,startCrop:{...cropRef.current}};
    }
  };
  const onMove=(e)=>{
    e.preventDefault();
    if(!dragRef.current) return;
    const canvas=canvasRef.current;
    const CW=canvas.width,CH=canvas.height;
    const{x:px,y:py}=getPos(e,canvas);
    const dx=px-dragRef.current.startX,dy=py-dragRef.current.startY;
    let{x,y,w,h}=dragRef.current.startCrop;
    if(dragRef.current.type==="move"){
      cropRef.current={x:clamp(x+dx,0,CW-w),y:clamp(y+dy,0,CH-h),w,h};
    } else {
      const id=dragRef.current.handle;
      let nx=x,ny=y,nw=w,nh=h;
      if(id==="tl"){nx=clamp(x+dx,0,x+w-MIN);ny=clamp(y+dy,0,y+h-MIN);nw=x+w-nx;nh=y+h-ny;}
      if(id==="tr"){nw=clamp(w+dx,MIN,CW-x);ny=clamp(y+dy,0,y+h-MIN);nh=y+h-ny;}
      if(id==="bl"){nx=clamp(x+dx,0,x+w-MIN);nw=x+w-nx;nh=clamp(h+dy,MIN,CH-y);}
      if(id==="br"){nw=clamp(w+dx,MIN,CW-x);nh=clamp(h+dy,MIN,CH-y);}
      if(id==="tm"){ny=clamp(y+dy,0,y+h-MIN);nh=y+h-ny;}
      if(id==="bm"){nh=clamp(h+dy,MIN,CH-y);}
      if(id==="ml"){nx=clamp(x+dx,0,x+w-MIN);nw=x+w-nx;}
      if(id==="mr"){nw=clamp(w+dx,MIN,CW-x);}
      if(ratio!==0&&["br","tl","tr","bl"].includes(id)){
        if(nw/nh>ratio)nh=nw/ratio;else nw=nh*ratio;
        nw=Math.min(nw,CW-nx);nh=Math.min(nh,CH-ny);
      }
      cropRef.current={x:nx,y:ny,w:nw,h:nh};
    }
    draw();
  };
  const onUp=()=>{dragRef.current=null;};

  const applyCrop=()=>{
    const canvas=canvasRef.current,img=imgRef.current;
    if(!canvas||!img) return;
    const{x,y,w,h}=cropRef.current;
    const sx=img.naturalWidth/canvas.width,sy=img.naturalHeight/canvas.height;
    const out=document.createElement("canvas");
    out.width=Math.round(w*sx);out.height=Math.round(h*sy);
    out.getContext("2d").drawImage(img,x*sx,y*sy,w*sx,h*sy,0,0,out.width,out.height);
    out.toBlob((blob)=>{
      if(blob) onDone(blob, URL.createObjectURL(blob), RATIOS.find(r=>r.value===ratio)?.label || "Free");
    },"image/jpeg",0.93);
  };

  return (
    <div className="crop-overlay">
      <div className="crop-modal">
        <div className="crop-topbar">
          <button className="crop-bar-btn" onClick={onCancel}>✕ Cancel</button>
          <span className="crop-modal-title">✂ Crop Image</span>
          <button className="crop-bar-btn crop-apply-btn" onClick={applyCrop}>Apply ✓</button>
        </div>
        <div className="crop-ratios">
          {RATIOS.map(r=>(
            <button key={r.label} className={`crop-ratio-pill${ratio===r.value?" active":""}`} onClick={()=>setRatio(r.value)}>
              <span>{r.icon}</span><span>{r.label}</span>
            </button>
          ))}
        </div>
        <div ref={containerRef} className="crop-canvas-wrap">
          <canvas ref={canvasRef} className="crop-canvas"
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
            onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
          />
        </div>
        <p className="crop-hint">Drag corners to resize · Drag inside to move</p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN AddBlog COMPONENT
// ══════════════════════════════════════════════════════════════
function AddBlog() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [toast,    setToast]    = useState(null);
  const [imageAspectRatio, setImageAspectRatio] = useState("4:5"); // sensible default
  const [hideComments, setHideComments] = useState(false);
  const [allowShare,   setAllowShare]   = useState(true);
  const [allowSave,    setAllowSave]    = useState(true);

  // ── BUY ME A TREAT ── the server decides whether the option may be used
  // (payout setup complete, or the one first-time access still available) and
  // refuses the publish otherwise; this state only drives the hint.
  const [treatsEnabled, setTreatsEnabled] = useState(false);
  const [payoutSetup, setPayoutSetup] = useState(null);
  const loadPayoutSetup = useCallback(() => {
    getPayoutSetup().then(setPayoutSetup).catch(() => setPayoutSetup(null));
  }, []);
  useEffect(() => { loadPayoutSetup(); }, [loadPayoutSetup]);

  // ── LANGUAGE + CATEGORY ── exactly one of each per blog. `defaultLanguage`
  // is the writer's saved default (pre-selected on every visit); `language`
  // is THIS blog's. Changing one never changes the other unless the writer
  // presses "Set as default".
  const [language, setLanguage] = useState("");
  const [category, setCategory] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [contentOptions, setContentOptions] = useState(FALLBACK_OPTIONS);
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);

  const [imagePreview, setImagePreview] = useState(null);
  const [originalImageBlob,setOriginalImageBlob]= useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(0);
  const [editTab, setEditTab] = useState("filters");
  const [adjust, setAdjust] = useState({
    brightness:100, contrast:100, saturation:100, warmth:0, fade:0, vignette:0,
  });

  const [videoPreview, setVideoPreview] = useState(null);
  const [videoTrim, setVideoTrim] = useState({ start:0, end:100 });
  const [videoVolume, setVideoVolume] = useState(100);
  const [videoFilter, setVideoFilter] = useState(0);
  const [showVideoEdit,setShowVideoEdit]= useState(false);

  const canvasRef = useRef(null);
  const imageRef = useRef();
  const videoRef = useRef();
  const videoPlayerRef = useRef();

  const token = localStorage.getItem("token");
  const BASE_URL = window.location.hostname === "localhost"
  ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.5 + 0.5,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i+1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${1 - dist/100})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(particles[i].x, particles[i].y, particles[i].r, 0, Math.PI*2);
        ctx.fillStyle = "rgba(167,139,250,0.6)";
        ctx.fill();
        particles[i].x += particles[i].vx;
        particles[i].y += particles[i].vy;
        if (particles[i].x < 0 || particles[i].x > canvas.width) particles[i].vx *= -1;
        if (particles[i].y < 0 || particles[i].y > canvas.height) particles[i].vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  useEffect(() => {
    const u = localStorage.getItem("username");
    if (u) setAuthor(u);
  }, []);

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // First visit (no saved default) → ask for a language. Later visits → the
  // saved default is simply pre-selected. If preferences cannot be loaded the
  // writer still picks a language from the form; nothing is assumed.
  useEffect(() => {
    getContentPreferences()
      .then((prefs) => {
        setContentOptions(prefs.options);
        if (prefs.defaultWritingLanguage) {
          setDefaultLanguage(prefs.defaultWritingLanguage);
          setLanguage((current) => current || prefs.defaultWritingLanguage);
        } else {
          setShowLanguagePrompt(true);
        }
      })
      .catch(() => {});
  }, []);

  const saveDefaultLanguage = async (lang) => {
    try {
      const saved = await saveContentPreferences({ defaultWritingLanguage: lang });
      setDefaultLanguage(saved.defaultWritingLanguage);
      return true;
    } catch (err) {
      showToast(err.response?.data?.error || "Couldn't save your default language", "error");
      return false;
    }
  };

  const chooseFirstLanguage = async (lang) => {
    setLanguage(lang);
    setShowLanguagePrompt(false);
    if (await saveDefaultLanguage(lang)) showToast(`${lang} saved as your default language`, "success");
  };

  const makeLanguageDefault = async () => {
    if (await saveDefaultLanguage(language)) showToast(`${language} is now your default language`, "success");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    setOriginalImageBlob(null);
    setCropSrc(url);
    setSelectedFilter(0);
    setAdjust({ brightness:100, contrast:100, saturation:100, warmth:0, fade:0, vignette:0 });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const videoEl = document.createElement("video");
    videoEl.src = URL.createObjectURL(file);
    videoEl.onloadedmetadata = () => {
      if (videoEl.duration > 60) {
        showToast("Video must be under 1 minute ❌", "error");
        videoRef.current.value = "";
        setVideo(null);
        setVideoPreview(null);
      } else {
        setVideo(file);
        setVideoPreview(URL.createObjectURL(file));
        setVideoTrim({ start:0, end:100 });
        setVideoVolume(100);
        setVideoFilter(0);
      }
    };
  };

  const buildFilter = buildImageFilter;

  const currentImgFilter = buildFilter(selectedFilter, adjust);
  const vigShadow = adjust.vignette > 0
  ? `inset 0 0 ${adjust.vignette*2.5}px ${adjust.vignette*0.8}px rgba(0,0,0,0.75)`
    : "none";

  const onCropDone = (blob, url, ratioLabel) => {
    setOriginalImageBlob(blob);
    setImagePreview(url);
    setShowCrop(false);
    setImage(new File([blob], image?.name || "cropped.jpg", { type:"image/jpeg" }));
    if (ratioLabel && ratioLabel!== "Free") setImageAspectRatio(ratioLabel);
  };

  const bakeImage = (srcUrl) => bakeEditedImage(srcUrl, currentImgFilter, adjust.vignette);

  // ── submit ──
  const createBlog = async (e) => {
    e.preventDefault();
    if (title   && title.length   > 100) return showToast("Title max 100 chars",   "error");
    if (content && content.length > 500) return showToast("Content max 500 chars", "error");
    if (!language) return showToast("Please choose the blog's language", "error");
    if (!category) return showToast("Please choose a category", "error");
    if (treatsEnabled && payoutSetup && !payoutSetup.complete && !payoutSetup.firstTimeAvailable) {
      return showToast("Complete Payout Setup to enable Buy Me a Treat on this post.", "error");
    }

    try {
      setLoading(true); setProgress(0);

      const formData = new FormData();
      if (title) formData.append("title", title);
      if (content) formData.append("content", content);
      if (author)  formData.append("author",  author);
      formData.append("language", language);
      formData.append("category", category);
      if (image)   formData.append("aspectRatio", imageAspectRatio);
      formData.append("hideComments", hideComments);
      formData.append("allowShare",   allowShare);
      formData.append("allowSave",    allowSave);
      formData.append("treatsEnabled", treatsEnabled);

      if (image && imagePreview) {
        const hasEdits = selectedFilter!== 0 || Object.values(adjust).some((v,i) => [100,100,100,0,0,0][i]!== v);
        if (hasEdits) {
          const baked = await bakeImage(imagePreview);
          formData.append("image", new File([baked], image.name, { type:"image/jpeg" }));
        } else {
          formData.append("image", image);
        }
      }

      if (video) formData.append("video", video);

      // SECURITY (10.1): attach the device fingerprint (additive form fields).
      try {
        const fp = await getDeviceFingerprint();
        if (fp.deviceHash) formData.append("deviceHash", fp.deviceHash);
        formData.append("clientFingerprint", JSON.stringify(fp.signals || {}));
      } catch (_) { /* never block publishing over fingerprinting */ }

      const res = await axios.post(`${BASE_URL}/api/blogs/create`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / p.total)),
      });

      if (res?.data?.treatNotice) showToast(res.data.treatNotice, "info");
      else showToast("Blog Published 🚀", "success");
      setTreatsEnabled(false);
      if (treatsEnabled) loadPayoutSetup();
      setTitle(""); setContent(""); setImage(null); setVideo(null);
      setImagePreview(null); setVideoPreview(null); setProgress(0);
      // The next blog starts from the default language again and must pick
      // its own category.
      setCategory(""); setLanguage(defaultLanguage || language);
      setSelectedFilter(0);
      setAdjust({ brightness:100, contrast:100, saturation:100, warmth:0, fade:0, vignette:0 });
      if(imageRef.current) imageRef.current.value = "";
      if(videoRef.current) videoRef.current.value = "";
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Upload Failed ❌", "error");
    } finally {
      setLoading(false);
    }
  };

  const titlePct   = (title.length   / 100) * 100;
  const contentPct = (content.length / 500) * 100;

  const adjSliders = ADJUST_SLIDERS;

  return (
    <div className="addblog-wrapper">
      <canvas ref={canvasRef} className="addblog-canvas" />
      <Navbar />

      {showCrop && cropSrc && (
        <CropEditor src={cropSrc} onDone={onCropDone} onCancel={() => setShowCrop(false)} />
      )}

      {toast && (
        <div className={`addblog-toast addblog-toast-${toast.type}`}>{toast.msg}</div>
      )}

      {/* FIRST VISIT — no default writing language saved yet */}
      {showLanguagePrompt && (
        <div className="crop-overlay" role="dialog" aria-modal="true" aria-labelledby="lang-prompt-title">
          <div className="lang-prompt-modal">
            <h3 id="lang-prompt-title">Which language do you write in?</h3>
            <p>We'll pre-select it for every new blog. You can change it any time.</p>
            <div className="lang-prompt-grid">
              {contentOptions.languages.map((l) => (
                <button type="button" key={l} className="crop-ratio-pill" onClick={() => chooseFirstLanguage(l)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="addblog-container">
        <div className="addblog-box">
          <BannerAds />
          <div className="addblog-orb orb1" />
          <div className="addblog-orb orb2" />

          <div className="addblog-header">
            <div className="addblog-badge">✦ Create</div>
            <h2 className="addblog-title">Write a Blog</h2>
            <p className="addblog-sub">Share your knowledge with the world</p>
          </div>

          <form onSubmit={createBlog} className="addblog-form">

            <div className="field-group">
              <label className="field-label">📝 Title <span className="optional">(optional)</span></label>
              <input
                type="text" placeholder="Enter your blog title..."
                value={title} maxLength={100}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading} className="addblog-input"
              />
              <div className="char-bar-row">
                <div className="char-bar"><div className="char-bar-fill" style={{ width:`${titlePct}%`, background: titlePct>90?"#ef4444":titlePct>70?"#f59e0b":"#a78bfa" }} /></div>
                <span className={`char-count${title.length>90?" error":title.length>70?" warning":""}`}>{title.length}/100</span>
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">💬 Content <span className="optional">(optional)</span></label>
              <textarea placeholder="Write your blog content here..." value={content} maxLength={500} rows={6} onChange={(e) => setContent(e.target.value)} disabled={loading} className="addblog-input addblog-textarea" />
              <div className="char-bar-row">
                <div className="char-bar"><div className="char-bar-fill" style={{ width:`${contentPct}%`, background: contentPct>90?"#ef4444":contentPct>70?"#f59e0b":"#06b6d4" }} /></div>
                <span className={`char-count${content.length>450?" error":content.length>350?" warning":""}`}>{content.length}/500</span>
              </div>
            </div>

            {/* ── BLOG LAYOUT ORDER (Part B item 3) ──
                The four content fields must read Title → Content → Image →
                Video, top to bottom, with nothing between them. Author and
                Post Settings used to sit here, between Content and Image,
                which broke that run; they have been moved BELOW Video. They
                are unchanged otherwise — same fields, same state, same
                handlers — and nothing about what this form submits changed. */}

            <div className="field-group">
              <label className="field-label">🖼 Image <span className="optional">(optional)</span></label>
              <div className="file-input-wrap">
                <input ref={imageRef} type="file" accept="image/*" onChange={handleImageChange} disabled={loading} className="addblog-file" id="imgInput" />
                <label htmlFor="imgInput" className="file-label">{image? `✅ ${image.name}` : "Choose image..."}</label>
              </div>

              {imagePreview && (
                <div className="media-editor">
                  <div className="media-editor-preview-wrap">
                    <img src={imagePreview} className="media-editor-preview" style={{ filter: currentImgFilter, boxShadow: vigShadow }} alt="preview" />
                    <button type="button" className="media-editor-crop-btn" onClick={() => { setCropSrc(originalImageBlob? URL.createObjectURL(originalImageBlob) : imagePreview); setShowCrop(true); }}>✂ Crop</button>
                  </div>
                  <div className="media-editor-tabs">
                    <button type="button" className={`med-tab${editTab==="filters"?" active":""}`} onClick={()=>setEditTab("filters")}>🎨 Filters</button>
                    <button type="button" className={`med-tab${editTab==="adjust"?" active":""}`} onClick={()=>setEditTab("adjust")}>🎛 Adjust</button>
                  </div>
                  {editTab === "filters" && (
                    <div className="med-filter-strip">
                      {FILTERS.map((f,i) => (
                        <button type="button" key={f.name} className={`med-filter-btn${selectedFilter===i?" active":""}`} onClick={() => setSelectedFilter(i)}>
                          <div className="med-filter-thumb" style={{ backgroundImage:`url(${imagePreview})`, filter:f.css }} />
                          <span className="med-filter-name">{f.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {editTab === "adjust" && (
                    <div className="med-adjust-grid">
                      {adjSliders.map(({ key, label, min, max, step }) => (
                        <div key={key} className="med-slider-row">
                          <div className="med-slider-label"><span>{label}</span><span className="med-slider-val">{adjust[key]}{["brightness","contrast","saturation"].includes(key)?"%":""}</span></div>
                          <input type="range" min={min} max={max} step={step} value={adjust[key]} className="med-slider" onChange={e => setAdjust(p=>({...p,[key]:Number(e.target.value)}))} />
                        </div>
                      ))}
                      <button type="button" className="med-reset-btn" onClick={() => setAdjust({brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0})}>↺ Reset</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">🎥 Video <span className="optional">(max 1 min)</span></label>
              <div className="file-input-wrap">
                <input ref={videoRef} type="file" accept="video/*" onChange={handleVideoChange} disabled={loading} className="addblog-file" id="vidInput" />
                <label htmlFor="vidInput" className="file-label">{video? `✅ ${video.name}` : "Choose video..."}</label>
              </div>

              {videoPreview && (
                <div className="media-editor">
                  <div className="media-editor-preview-wrap">
                    <video ref={videoPlayerRef} src={videoPreview} className="media-editor-preview media-editor-video" style={{ filter: FILTERS[videoFilter].css }} controls />
                  </div>
                  <button type="button" className={`med-tab video-edit-toggle${showVideoEdit?" active":""}`} onClick={() => setShowVideoEdit(v=>!v)}>🎬 Edit Video {showVideoEdit? "▲" : "▼"}</button>
                  {showVideoEdit && (
                    <div className="med-video-editor">
                      <p className="med-section-label">🎨 Filters</p>
                      <div className="med-filter-strip">
                        {FILTERS.map((f,i) => (
                          <button type="button" key={f.name} className={`med-filter-btn${videoFilter===i?" active":""}`} onClick={() => setVideoFilter(i)}>
                            <div className="med-filter-thumb med-filter-thumb-video" style={{ background: i===0?"#333":`linear-gradient(135deg,#${Math.floor(Math.random()*0xffffff).toString(16).padStart(6,"0")},#444)`, filter:f.css }} />
                            <span className="med-filter-name">{f.name}</span>
                          </button>
                        ))}
                      </div>
                      <div className="med-slider-row" style={{marginTop:12}}>
                        <div className="med-slider-label"><span>🔊 Volume</span><span className="med-slider-val">{videoVolume}%</span></div>
                        <input type="range" min={0} max={100} step={1} value={videoVolume} className="med-slider" onChange={e => { setVideoVolume(Number(e.target.value)); if (videoPlayerRef.current) videoPlayerRef.current.volume = Number(e.target.value)/100; }} />
                      </div>
                      <div className="med-trim-note"><span>✂</span><span>Video trimming is applied server-side. The preview shows your filter selection.</span></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── LANGUAGE + CATEGORY ── one of each, both required. Placed
                after Video so Title → Content → Image → Video stays
                uninterrupted (Part B item 3). */}
            <div className="field-group">
              <label className="field-label" htmlFor="blogLanguage">🌐 Language</label>
              <select
                id="blogLanguage"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={loading}
                className="addblog-input addblog-select"
              >
                <option value="">Select a language…</option>
                {contentOptions.languages.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              {language && language !== defaultLanguage && (
                <div className="lang-default-row">
                  <span>{defaultLanguage ? `Your default is ${defaultLanguage}.` : "No default language saved."}</span>
                  <button type="button" className="lang-default-btn" onClick={makeLanguageDefault} disabled={loading}>
                    Set {language} as default
                  </button>
                </div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="blogCategory">🏷 Category</label>
              <select
                id="blogCategory"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
                className="addblog-input addblog-select"
              >
                <option value="">Select a category…</option>
                {contentOptions.categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* ── AUTHOR + POST SETTINGS ──
                Moved down from between Content and Image so the four content
                fields run Title → Content → Image → Video uninterrupted
                (Part B item 3). Neither field changed. */}

            <div className="field-group">
              <label className="field-label">👤 Author</label>
              <input type="text" value={author} readOnly className="addblog-input addblog-readonly" />
            </div>

            <div className="field-group">
              <label className="field-label">⚙ Post Settings</label>
              <div className="post-settings-row">
                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={hideComments}
                    onChange={(e) => setHideComments(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Hide comments</span>
                </label>

                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={!allowShare}
                    onChange={(e) => setAllowShare(!e.target.checked)}
                    disabled={loading}
                  />
                  <span>Turn off sharing</span>
                </label>

                <label className="post-toggle">
                  <input
                    type="checkbox"
                    checked={!allowSave}
                    onChange={(e) => setAllowSave(!e.target.checked)}
                    disabled={loading}
                  />
                  <span>Turn off saving</span>
                </label>
              </div>
            </div>

            {/* ── BUY ME A TREAT ── */}
            <div className="field-group treat-enable">
              <label className="post-toggle">
                <input
                  type="checkbox"
                  checked={treatsEnabled}
                  onChange={(e) => setTreatsEnabled(e.target.checked)}
                  disabled={loading}
                />
                <span>🎁 Enable Buy Me a Treat</span>
              </label>
              <p className="treat-enable-desc">Readers can send you treats to appreciate your writing.</p>
              {payoutSetup && !payoutSetup.complete && (
                <p className="treat-enable-warn" role="note">
                  {payoutSetup.firstTimeAvailable
                    ? "Complete Payout Setup to receive treats. Your first treat-enabled post is allowed without it, but the next one won't be."
                    : "Complete Payout Setup — a post with Buy Me a Treat enabled can't be published until your bank details are added."}{" "}
                  <a href="/treat-payouts">Complete Payout Setup →</a>
                </p>
              )}
            </div>

            {loading && (
              <div className="upload-progress">
                <div className="upload-progress-bar"><div className="upload-progress-fill" style={{ width:`${progress}%` }} /></div>
                <span className="upload-progress-text">{progress}% uploaded</span>
              </div>
            )}

            <button type="submit" className="addblog-btn" disabled={loading || !language || !category}>
              {loading? <><span className="btn-spinner" /> Publishing {progress}%</> : <>🚀 Publish Blog</>}
            </button>
            {!loading && (!language || !category) && (
              <p className="publish-hint">
                Choose {!language && !category ? "a language and a category" : !language ? "a language" : "a category"} to publish.
              </p>
            )}

          </form>
        </div>
      </div>
    </div>
  );
}

export default AddBlog;